"use client";
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useContactModal } from '@/contexts/ContactModalContext';

const IconUnderstand = ({ color }: { color: string }) => (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
        <circle cx="15" cy="15" r="11.5" stroke={color} strokeWidth="2.2" />
        <path d="M15 20v-6M15 10.5v-.1" stroke={color} strokeWidth="2.4" strokeLinecap="round" />
    </svg>
);
const IconAudit = ({ color }: { color: string }) => (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
        <circle cx="13" cy="13" r="8.5" stroke={color} strokeWidth="2.2" />
        <path d="M19 19l6 6" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
);
const IconStructure = ({ color }: { color: string }) => (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
        <circle cx="15" cy="15" r="4" stroke={color} strokeWidth="2.2" />
        <circle cx="15" cy="4" r="2.3" stroke={color} strokeWidth="2.2" />
        <circle cx="25" cy="15" r="2.3" stroke={color} strokeWidth="2.2" />
        <circle cx="15" cy="26" r="2.3" stroke={color} strokeWidth="2.2" />
        <circle cx="5" cy="15" r="2.3" stroke={color} strokeWidth="2.2" />
        <path d="M15 11v-4M19 15h4M15 19v4M11 15H7" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);
const IconBuild = ({ color }: { color: string }) => (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
        <path d="M17 3L6 17.5h6.5L11 27l11-14.5h-6.5L17 3z" fill={color} />
    </svg>
);
const IconStrengthen = ({ color }: { color: string }) => (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
        <rect x="3" y="11" width="11" height="8" rx="4" stroke={color} strokeWidth="2.2" />
        <rect x="16" y="11" width="11" height="8" rx="4" stroke={color} strokeWidth="2.2" />
        <path d="M11 15h8" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
);
const IconMonitor = ({ color }: { color: string }) => (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
        <circle cx="15" cy="15" r="4" stroke={color} strokeWidth="2.2" />
        <path d="M15 2v4M15 24v4M2 15h4M24 15h4M6.5 6.5l2.8 2.8M20.7 20.7l2.8 2.8M23.5 6.5l-2.8 2.8M9.3 20.7l-2.8 2.8" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const steps = [
    { num: '01', title: 'Understand', desc: 'Map your brand, expertise, audience, products/services and existing search presence.', Icon: IconUnderstand },
    { num: '02', title: 'Audit', desc: 'Identify gaps across technical SEO, content, entities, internal linking and information architecture.', Icon: IconAudit },
    { num: '03', title: 'Structure', desc: 'Organise the website around clear topics, entities and relationships.', Icon: IconStructure },
    { num: '04', title: 'Build', desc: 'Create original, evidence-led content that answers real questions and demonstrates expertise.', Icon: IconBuild },
    { num: '05', title: 'Strengthen', desc: 'Improve internal connections, supporting content, structured information and authoritative references.', Icon: IconStrengthen },
    { num: '06', title: 'Monitor', desc: 'Track changes in search visibility and emerging AI-search opportunities as the search landscape evolves.', Icon: IconMonitor },
];

const AUTOPLAY_MS = 4000;

export default function AISEOApproach() {
    const { openModal } = useContactModal();
    const [active, setActive] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        timerRef.current = setInterval(() => {
            setActive((prev) => (prev + 1) % steps.length);
        }, AUTOPLAY_MS);
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, [active]);

    const select = (i: number) => {
        if (timerRef.current) clearInterval(timerRef.current);
        setActive(i);
    };

    const cur = steps[active];

    return (
        <section className="relative overflow-hidden py-16 md:py-28 px-4 md:px-16 lg:px-24" style={{ background: '#0f0f0f' }}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, rgba(206,255,26,1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

            <div className="max-w-[1000px] mx-auto flex flex-col gap-14 md:gap-16 relative z-10">

                {/* Header */}
                <div className="flex flex-col items-center gap-4 text-center">
                    <motion.span initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.25em] uppercase"
                        style={{ color: 'rgba(206,255,26,0.6)', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}>
                        How We Work
                    </motion.span>
                    <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }}
                        className="text-2xl md:text-6xl font-bold tracking-tight leading-tight"
                        style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', color: '#ffffff' }}>
                        Our <span style={{ color: '#ceff1a' }}>AI Search Approach</span>
                    </motion.h2>
                </div>

                {/* Horizontal step selector */}
                <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
                    {steps.map((step, i) => (
                        <React.Fragment key={i}>
                            <button
                                onClick={() => select(i)}
                                className="relative flex flex-col items-center gap-2 group"
                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                            >
                                <motion.div
                                    animate={{
                                        background: active === i ? '#ceff1a' : 'rgba(255,255,255,0.05)',
                                        borderColor: active === i ? '#ceff1a' : 'rgba(255,255,255,0.15)',
                                        scale: active === i ? 1.1 : 1,
                                    }}
                                    transition={{ duration: 0.3 }}
                                    className="w-9 h-9 md:w-12 md:h-12 rounded-full flex items-center justify-center font-black font-mono text-xs md:text-sm"
                                    style={{
                                        border: '2px solid',
                                        color: active === i ? '#080400' : 'rgba(255,255,255,0.5)',
                                        boxShadow: active === i ? '0 0 24px rgba(206,255,26,0.5)' : 'none',
                                    }}
                                >
                                    {step.num}
                                </motion.div>
                                <span
                                    className="hidden md:block text-xs font-semibold uppercase tracking-wide transition-colors duration-300"
                                    style={{ color: active === i ? '#ceff1a' : 'rgba(255,255,255,0.35)', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                                >
                                    {step.title}
                                </span>
                            </button>
                            {i < steps.length - 1 && (
                                <div className="w-6 md:w-10 h-[2px] relative overflow-hidden rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
                                    <motion.div
                                        className="absolute inset-y-0 left-0"
                                        style={{ background: '#ceff1a' }}
                                        animate={{ width: active > i ? '100%' : '0%' }}
                                        transition={{ duration: 0.4 }}
                                    />
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                </div>

                {/* Active step panel */}
                <div className="relative" style={{ minHeight: 220 }}>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={active}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -16 }}
                            transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                            className="relative rounded-3xl p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-6 md:gap-10 overflow-hidden"
                            style={{ background: 'rgba(206,255,26,0.04)', border: '1px solid rgba(206,255,26,0.2)' }}
                        >
                            <span className="absolute select-none pointer-events-none" style={{
                                bottom: -30, right: 10, fontSize: 180, fontWeight: 900, lineHeight: 1,
                                color: 'rgba(206,255,26,0.04)', fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                            }}>{cur.num}</span>

                            <div
                                className="w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center shrink-0 relative z-10"
                                style={{ background: '#ceff1a' }}
                            >
                                <cur.Icon color="#080400" />
                            </div>

                            <div className="relative z-10 flex flex-col gap-3">
                                <span className="text-xs font-black font-mono" style={{ color: '#ceff1a', letterSpacing: '0.1em' }}>
                                    STEP {cur.num} / {String(steps.length).padStart(2, '0')}
                                </span>
                                <h3 className="text-2xl md:text-4xl font-bold" style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', color: '#ffffff', letterSpacing: '0.01em' }}>
                                    {cur.title}
                                </h3>
                                <p className="text-sm md:text-lg leading-relaxed max-w-xl" style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(255,255,255,0.6)' }}>
                                    {cur.desc}
                                </p>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* Search Console note */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="rounded-2xl px-6 py-5"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                    <p
                        className="text-sm md:text-base leading-relaxed text-center"
                        style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(255,255,255,0.6)', letterSpacing: '0.01em' }}
                    >
                        Google has also begun rolling out dedicated Search Console reporting for visibility in generative AI features, giving eligible sites more direct visibility into performance in AI search experiences.
                    </p>
                </motion.div>

                {/* CTA */}
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="flex justify-center">
                    <button onClick={openModal} className="inline-flex items-center justify-center whitespace-nowrap px-6 md:px-10 py-4 md:py-5 rounded-full font-semibold text-sm sm:text-base md:text-lg transition-all duration-300 hover:scale-105"
                        style={{ background: '#ceff1a', color: '#080400', fontFamily: 'var(--font-clash), "Clash Display", sans-serif', letterSpacing: '0.08em' }}>
                        Get an AI Search Audit →
                    </button>
                </motion.div>
            </div>
        </section>
    );
}
